import * as joint from '@clientio/rappid';

import { FLOWCHART_REPLACE_ICON_SVG } from './cached-icons';
import { FlowchartConfig, PlusIcon } from '../base/flowchart.config';

export const generateLeftButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '0%',
    y: '50%',
    offset: { x: -FlowchartConfig.plus.offset, y: 0 },
    attributes: {
      'data-testid': 'flowchart-buttons_left-add-icon',
    },
    markup: PlusIcon.generatePlusIcon(
      FlowchartConfig.plus.inactive,
      FlowchartConfig.plus.inside,
      FlowchartConfig.plus.inside,
      'left-add-icon',
    ),
  });
};

export const generateRightButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '100%',
    y: '50%',
    offset: { x: FlowchartConfig.plus.offset, y: 0 },
    attributes: {
      'data-testid': 'flowchart-buttons_right-add-icon',
    },
    markup: PlusIcon.generatePlusIcon(
      FlowchartConfig.plus.inactive,
      FlowchartConfig.plus.inside,
      FlowchartConfig.plus.inside,
      'right-add-icon',
    ),
  });
};

export const generateBottomButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '50%',
    y: '100%',
    offset: { x: 0, y: FlowchartConfig.plus.offset },
    attributes: {
      'data-testid': 'flowchart-buttons_bottom-add-icon',
    },
    markup: PlusIcon.generatePlusIcon(
      FlowchartConfig.plus.active,
      FlowchartConfig.plus.inside,
      FlowchartConfig.plus.inside,
      'bottom-add-icon',
    ),
  });
};

export const generateTopButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '50%',
    y: '0%',
    offset: { x: 0, y: -FlowchartConfig.plus.offset },
    attributes: {
      'data-testid': 'flowchart-buttons_top-add-icon',
    },
    markup: PlusIcon.generatePlusIcon(
      FlowchartConfig.plus.inactive,
      FlowchartConfig.plus.inside,
      FlowchartConfig.plus.inside,
      'top-add-icon',
    ),
  });
};

export const generateResetButtonStart = (tooltipText: string): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '50%',
    y: -(FlowchartConfig.replaceButton.size + FlowchartConfig.replaceButton.offset),
    className: 'joint-context-toolbar joint-theme-default element-activities reset-component-tool',
    attributes: {
      'data-tooltip-reset': 'Reset Component',
      tooltip: `${tooltipText}`,
      'data-testid': 'flowchart-buttons_reset-button-start',
    },
    offset: { x: -FlowchartConfig.replaceButton.size / 2, y: 0 },
    markup: joint.util.svg`${FLOWCHART_REPLACE_ICON_SVG}`,
  });
};

export const generateResetButtonRegular = (cellView: joint.dia.CellView, tooltipText: string): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: cellView?.model.attributes.isSelectionBehavior ? '16%' : '25%',
    y: -(FlowchartConfig.replaceButton.size + FlowchartConfig.replaceButton.offset),
    className: 'joint-context-toolbar joint-theme-default element-activities',
    attributes: {
      'data-tooltip-reset': 'Reset Component',
      tooltip: `${tooltipText}`,
      'data-testid': 'flowchart-buttons_reset-button-regular',
    },
    offset: { x: -FlowchartConfig.replaceButton.size / 2, y: 0 },
    markup: joint.util.svg`${FLOWCHART_REPLACE_ICON_SVG}`,
  });
};

export const generateAddCommentButtonRegular = (cellView: joint.dia.CellView, tooltipText: string): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: cellView?.model.attributes.isSelectionBehavior ? '16%' : '25%',
    y: -(FlowchartConfig.replaceButton.size + FlowchartConfig.replaceButton.offset),
    className: 'joint-context-toolbar joint-theme-default element-activities',
    attributes: {
      'data-tooltip-comment': 'Add Comment',
      tooltip: `${tooltipText}`,
      'data-testid': 'flowchart-buttons_comment-button-regular',
    },
    offset: { x: -FlowchartConfig.replaceButton.size / 2, y: 0 },
    markup: joint.util.svg`${FLOWCHART_REPLACE_ICON_SVG}`,
  });
};

export const generateContainerAddButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '50%',
    y: '100%',
    offset: { x: 0, y: -30 },
    markup: [
      {
        tagName: 'rect',
        attributes: {
          width: 20,
          height: 20,
          fill: '#063D70',
          rx: 20,
          ry: 20,
          x: -10,
          y: -10,
          cursor: 'pointer',
        },
      },
      {
        tagName: 'path',
        selector: 'subflow icon',
        attributes: {
          d: 'M0,-1 V1 M-1,0 H1',
          stroke: 'white',
          'stroke-width': FlowchartConfig.arrow.strokeWidth,
          cursor: 'pointer',
          'data-testid': 'flowchart-buttons_container-add-button',
        },
      },
    ],
  });
};

export const generateSubflowAddButton = (): joint.elementTools.Button => {
  return new joint.elementTools.Button({
    focusOpacity: 0.5,
    x: '50%',
    y: '0%',
    offset: { x: 0, y: 40 },
    markup: [
      {
        tagName: 'rect',
        attributes: {
          width: 40,
          height: 40,
          fill: '#E3EDF4',
          rx: 5,
          ry: 5,
          x: -20,
          y: -20,
          cursor: 'pointer',
        },
      },
      {
        tagName: 'rect',
        attributes: {
          width: 20,
          height: 20,
          fill: '#063D70',
          rx: 20,
          ry: 20,
          x: -10,
          y: -10,
          cursor: 'pointer',
        },
      },
      {
        tagName: 'path',
        selector: 'subflow icon',
        attributes: {
          d: 'M0,-1 V1 M-1,0 H1',
          stroke: 'white',
          'stroke-width': FlowchartConfig.arrow.strokeWidth,
          cursor: 'pointer',
        },
      },
    ],
  });
};
